import { CaseDataField } from '@telespot/domain';

/**
 * The persisted JSON shape of one case-data field, as stored in the CaseType
 * `fields` column (the legacy `FieldType` / `ICaseFieldDetails` contract).
 *
 * The discriminator (`type`), the internal `name`, and the `required` /
 * `encrypted` flags live at the top level; every type-specific property is
 * nested under `details`, where some keys are snake_case (`display_name`,
 * `max_lines`, `max_length`) for backwards compatibility with data written by
 * the old editor. This mapper is the single place that knows that contract.
 */
export interface PersistedCaseDataField {
  type: string;
  name: string;
  details: Record<string, unknown>;
  required?: boolean;
  encrypted?: boolean;
}

/**
 * Maps between the domain {@link CaseDataField} discriminated union and the
 * persisted `fields` JSON of a case type. Unlike the sibling mappers it works
 * on plain JSON (not `Parse.Object`s), so it needs no Parse instance and both
 * directions are static. `ParseCaseTypeMapper` applies it per field, so the
 * persisted contract never leaves this repository layer.
 */
export class ParseCaseDataFieldMapper {
  /**
   * Project a persisted field to its domain variant. Reads the snake_case
   * detail keys (normalizing legacy `null`s to `undefined`), defaults missing
   * `required` / `encrypted` to `false`, falls back `displayName` to the
   * internal `name` when no `display_name` was stored, and maps an unknown
   * `type` to a `label` (the safe inert variant). Inverse of
   * {@link fromDomain}.
   */
  public static toDomain(field: PersistedCaseDataField): CaseDataField {
    const details = field.details ?? {};
    const base = {
      name: field.name,
      displayName: (details['display_name'] as string) ?? field.name,
      required: field.required ?? false,
      encrypted: field.encrypted ?? false,
    };

    switch (field.type) {
      case 'textedit':
        return {
          ...base,
          type: 'textedit',
          multiline: (details['multiline'] as boolean | null | undefined) ?? undefined,
          placeholder: (details['placeholder'] as string | null | undefined) ?? undefined,
          maxLines: (details['max_lines'] as number | null | undefined) ?? undefined,
          maxLength: (details['max_length'] as number | null | undefined) ?? undefined,
        };
      case 'number':
        return {
          ...base,
          type: 'number',
          decimals: (details['decimals'] as number | null | undefined) ?? undefined,
          min: (details['min'] as number | null | undefined) ?? undefined,
          max: (details['max'] as number | null | undefined) ?? undefined,
          units: (details['units'] as string | null | undefined) ?? undefined,
        };
      case 'boolean':
        return { ...base, type: 'boolean', default: (details['default'] as boolean | null | undefined) ?? undefined };
      case 'switcher':
        return {
          ...base,
          type: 'switcher',
          options: (details['options'] as string[]) ?? [],
          multiple: (details['multiple'] as boolean | null | undefined) ?? undefined,
        };
      case 'selection':
        return {
          ...base,
          type: 'selection',
          options: (details['options'] as string[]) ?? [],
          multiple: (details['multiple'] as boolean | null | undefined) ?? undefined,
        };
      case 'sectionHeader':
        return { ...base, type: 'sectionHeader' };
      case 'label':
        return { ...base, type: 'label' };
      case 'case_name':
        return { ...base, type: 'case_name' };
      case 'date':
        return { ...base, type: 'date' };
      case 'location':
        return { ...base, type: 'location' };
      default:
        return { ...base, type: 'label' };
    }
  }

  /**
   * Project a domain field to its persisted JSON. Type-specific properties are
   * nested under `details` (snake_case where the legacy contract demands it);
   * absent optional properties are omitted so the blob stays minimal. Inverse
   * of {@link toDomain}.
   */
  public static fromDomain(field: CaseDataField): PersistedCaseDataField {
    const details: Record<string, unknown> = { display_name: field.displayName };

    switch (field.type) {
      case 'textedit':
        if (field.multiline !== undefined) details['multiline'] = field.multiline;
        if (field.placeholder !== undefined) details['placeholder'] = field.placeholder;
        if (field.maxLines !== undefined) details['max_lines'] = field.maxLines;
        if (field.maxLength !== undefined) details['max_length'] = field.maxLength;
        break;
      case 'number':
        if (field.decimals !== undefined) details['decimals'] = field.decimals;
        if (field.min !== undefined) details['min'] = field.min;
        if (field.max !== undefined) details['max'] = field.max;
        if (field.units !== undefined) details['units'] = field.units;
        break;
      case 'boolean':
        if (field.default !== undefined) details['default'] = field.default;
        break;
      case 'selection':
      case 'switcher':
        details['options'] = [...field.options];
        if (field.multiple !== undefined) details['multiple'] = field.multiple;
        break;
      // sectionHeader | label | case_name | date | location -> base details only
    }

    return {
      type: field.type,
      name: field.name,
      details,
      required: field.required,
      encrypted: field.encrypted,
    };
  }
}
